import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Pagination, 
  Spin, 
  Row, 
  Col, 
  Card, 
  Statistic, 
  Select, 
  DatePicker, 
  Button, 
  Space,
  Input,
  Tag,
  Tooltip,
  Modal,
  notification,
  Table,
  Descriptions
} from 'antd';
import { 
  ReloadOutlined, 
  FilterOutlined, 
  ClearOutlined, 
  DeleteOutlined,
  EyeOutlined 
} from '@ant-design/icons';
import { fetchLogStats, cleanupOldLogs } from '../../actions/logs';
import { useLogsPagination } from '../../hooks/useLogsPagination';
import DialogCustom from '../../components/dialog';
import Accordion from '../../components/accordion';
import Bread from '../../components/breadcrumb';
import { routeLogs } from '../../utils/routesBread';
import { 
  parserLogs, 
  columnsLogs, 
  moduloOptions, 
  accionOptions,
  getModuloColor,
  getAccionColor,
  formatDateRange 
} from '../../utils/parsers/logs';
import './logs.scss';

// --- Ocultar el segundo panel del RangePicker (solo mostrar un mes) ---
const hideSecondPanelStyle = `
  .ant-picker-panels {
    display: flex !important;
  }
  .ant-picker-panels > *:nth-child(2) {
    display: none !important;
  }
  /* Forzar ambos botones de navegación visibles y habilitados */
  .ant-picker-header-super-next-btn,
  .ant-picker-header-next-btn,
  .ant-picker-header-super-prev-btn,
  .ant-picker-header-prev-btn {
    display: inline-block !important;
    pointer-events: auto !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
  /* Asegurar que el header del calendario esté visible y bien posicionado */
  .ant-picker-panel > .ant-picker-header {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: auto !important;
    min-height: 32px !important;
    padding: 0 8px !important;
    background: #fff !important;
    position: static !important;
    z-index: 1;
    width: 100%;
  }
  .ant-picker-header-view {
    display: flex !important;
    align-items: center !important;
    gap: 4px !important;
    justify-content: center !important;
    width: auto;
    flex: 1;
  }
`;
if (typeof window !== 'undefined' && !document.getElementById('hide-second-panel-style')) {
  const style = document.createElement('style');
  style.id = 'hide-second-panel-style';
  style.innerHTML = hideSecondPanelStyle;
  document.head.appendChild(style);
}

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Search } = Input;

const Logs = () => {
  const dispatch = useDispatch();
  const { stats } = useSelector(state => state.logsReducer);
  const {
    logs,
    total,
    loading,
    error,
    page,
    pageSize,
    filters,
    handlePageChange,
    handleFilterChange,
    clearFilters,
    loadLogs
  } = useLogsPagination();
  
  const [showFilters, setShowFilters] = useState(false);
  const [cleanupModal, setCleanupModal] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);
  const [logSeleccionado, setLogSeleccionado] = useState(null);

  useEffect(() => {
    loadStats();
  }, [filters]);

  const loadStats = () => {
    dispatch(fetchLogStats(filters));
  };

  const handleSingleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    if (value === undefined || value === null || value === '') {
      delete newFilters[key];
    }
    handleFilterChange(newFilters);
  };

  const handleDateRangeChange = (dates) => {
    const newFilters = { ...filters };
    if (dates) {
      const fechaInicio = dates[0].format('YYYY-MM-DD');
      const fechaFin = dates[1].format('YYYY-MM-DD');
      newFilters.fechaInicio = fechaInicio;
      newFilters.fechaFin = fechaFin;
    } else {
      delete newFilters.fechaInicio;
      delete newFilters.fechaFin;
    }
    handleFilterChange(newFilters);
  };

  const handleCleanup = () => {
    dispatch(cleanupOldLogs(cleanupDays, () => {
      setCleanupModal(false);
      loadLogs();
      loadStats();
      notification.success({
        message: 'Logs limpiados',
        description: `Se eliminaron los logs de más de ${cleanupDays} días`
      });
    }));
  };

  const showLogDetail = (log) => {
    setLogSeleccionado(log);
  };

  const enhancedColumnsLogs = [
    ...columnsLogs,
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Ver detalles">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            onClick={() => showLogDetail(record)}
          />
        </Tooltip>
      ),
    }
  ];

  return (
    <div className="page-container">
      <div className="header-nav">
        <Bread routes={routeLogs} />
      </div>

      <div className="page-container__box">
        {/* Estadísticas */}
        {stats && (
          <Row gutter={[16, 16]} className="logs-stats">
            <Col xs={12} sm={12} md={6} lg={6}>
              <Card>
                <Statistic
                  title="Total de Logs"
                  value={stats.totalLogs}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6} lg={6}>
              <Card>
                <Statistic
                  title="Usuarios Activos"
                  value={stats.usuariosMasActivos ? stats.usuariosMasActivos.length : 0}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6} lg={6}>
              <Card>
                <Statistic
                  title="Módulos Registrados"
                  value={stats.logsPorModulo ? Object.keys(stats.logsPorModulo).length : 0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6} lg={6}>
              <Card>
                <Statistic
                  title="Acciones Registradas"
                  value={stats.logsPorAccion ? Object.keys(stats.logsPorAccion).length : 0}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Filtros */}
        <Card className="logs-filters">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div className="logs-filters-header">
              <Space wrap>
                <Button 
                  icon={<FilterOutlined />}
                  onClick={() => setShowFilters(!showFilters)}
                  type={showFilters ? 'primary' : 'default'}
                >
                  <span className="logs-filter-text">
                    {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
                  </span>
                  <span className="logs-filter-text-short">
                    Filtros
                  </span>
                </Button>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => { loadLogs(); loadStats(); }}
                >
                  <span className="logs-action-text">Actualizar</span>
                </Button>
                <Button 
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => setCleanupModal(true)}
                >
                  <span className="logs-action-text">Limpiar</span>
                </Button>
              </Space>
            </div>

            {showFilters && (
              <div className="logs-filters-content">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <Select
                      placeholder="Módulo"
                      style={{ width: '100%' }}
                      allowClear
                      onChange={(value) => handleSingleFilterChange('modulo', value)}
                      value={filters.modulo}
                      getPopupContainer={(trigger) => trigger.parentNode}
                    >
                      {moduloOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <Select
                      placeholder="Acción"
                      style={{ width: '100%' }}
                      allowClear
                      onChange={(value) => handleSingleFilterChange('accion', value)}
                      value={filters.accion}
                      getPopupContainer={(trigger) => trigger.parentNode}
                    >
                      {accionOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <Search
                      placeholder="Buscar usuario (nombre o email)"
                      allowClear
                      onSearch={(value) => handleSingleFilterChange('usuario', value?.trim())}
                      onPressEnter={(e) => handleSingleFilterChange('usuario', e.target.value?.trim())}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={6}>
                    <RangePicker
                      style={{ width: '100%' }}
                      onChange={handleDateRangeChange}
                      format="DD/MM/YYYY"
                      placeholder={['Fecha inicio', 'Fecha fin']}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      panelRender={panel => panel}
                      // Forzar solo un mes visible
                      showOneCalendar
                    />
                  </Col>
                </Row>
              </div>
            )}

            {Object.keys(filters).length > 0 && (
              <div className="logs-active-filters">
                <Space wrap>
                  <span>Filtros activos:</span>
                  {filters.modulo && <Tag color={getModuloColor(filters.modulo)}>{filters.modulo}</Tag>}
                  {filters.accion && <Tag color={getAccionColor(filters.accion)}>{filters.accion}</Tag>}
                  {filters.usuario && <Tag color="blue">Usuario: {filters.usuario}</Tag>}
                  {(filters.fechaInicio || filters.fechaFin) && (
                    <Tag color="green">{formatDateRange(filters.fechaInicio, filters.fechaFin)}</Tag>
                  )}
                  <Button 
                    size="small" 
                    icon={<ClearOutlined />}
                    onClick={clearFilters}
                  >
                    Limpiar
                  </Button>
                </Space>
              </div>
            )}
          </Space>
        </Card>

        {/* Lista de Logs */}
        <Accordion keyPanel="logs" header="Logs del Sistema">
          {loading ? (
            <div className="container-spin">
              <Spin tip="Cargando logs..." />
            </div>
          ) : error ? (
            <div className="error-message">
              <p>Error: {error}</p>
              <Button onClick={loadLogs}>Reintentar</Button>
            </div>
          ) : (
            <div className="logs-table-container">
              {/* Vista desktop */}
              <div className="logs-table-desktop">
                <Table
                  columns={enhancedColumnsLogs}
                  dataSource={logs ? logs.map(log => parserLogs(log)).filter(log => log !== null) : []}
                  pagination={false}
                  size="small"
                  locale={{
                    emptyText: 'No hay logs para mostrar'
                  }}
                  scroll={{ x: 'max-content' }}
                />
              </div>

              {/* Vista móvil */}
              <div className="logs-table-mobile">
                {logs && logs.length > 0 ? (
                  logs.map(log => {
                    const parsedLog = parserLogs(log);
                    if (!parsedLog) return null;
                    return (
                      <div key={parsedLog.key} className="mobile-log-card">
                        <div className="card-header">
                          <div className="log-time">
                            {parsedLog.fecha}
                          </div>
                          <div className="log-user">
                            <strong>{parsedLog.usuario}</strong>
                          </div>
                        </div>
                        
                        <div className="card-body">
                          <div className="log-tags">
                            <Tag color={getModuloColor(parsedLog.modulo)} size="small">
                              {parsedLog.modulo}
                            </Tag>
                            <Tag color={getAccionColor(parsedLog.accion)} size="small">
                              {parsedLog.accion}
                            </Tag>
                          </div>
                          
                          <div className="log-details">
                            {parsedLog.recursoNombre && (
                              <div className="detail-item">
                                <span className="label">Recurso:</span>
                                <span className="value">{parsedLog.recursoNombre}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="log-message">
                            {parsedLog.descripcion}
                          </div>
                          
                          <div className="card-actions">
                            <Button 
                              type="primary" 
                              size="small"
                              icon={<EyeOutlined />} 
                              onClick={() => showLogDetail(parsedLog)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-nothing">
                    No hay logs para mostrar
                  </div>
                )}
              </div>
            </div>
          )}
        </Accordion>

        {/* Paginación */}
        {logs && logs.length > 0 && (
          <div className="page-container__pagination">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={handlePageChange}
              showSizeChanger={false}
              showQuickJumper
              showTotal={(total, range) => 
                `${range[0]}-${range[1]} de ${total} logs`
              }
            />
          </div>
        )}
      </div>

      {/* Modal de detalles del log */}
      <DialogCustom
        title="Detalles del Log"
        open={logSeleccionado !== null}
        onClose={() => setLogSeleccionado(null)}
        maxWidth="md"
      >
        <div style={{ padding: '20px' }}>
          {logSeleccionado && (
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Fecha">
                {logSeleccionado.fecha}
              </Descriptions.Item>
              <Descriptions.Item label="Usuario">
                {logSeleccionado.usuario}
              </Descriptions.Item>
              <Descriptions.Item label="Módulo">
                <Tag color={getModuloColor(logSeleccionado.modulo)}>
                  {logSeleccionado.modulo}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Acción">
                <Tag color={getAccionColor(logSeleccionado.accion)}>
                  {logSeleccionado.accion}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Recurso">
                {logSeleccionado.recursoNombre || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Descripción">
                <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {logSeleccionado.descripcion}
                </div>
              </Descriptions.Item>
              {logSeleccionado.detalles && (
                <Descriptions.Item label="Detalles">
                  <div style={{ 
                    wordBreak: 'break-word', 
                    whiteSpace: 'pre-wrap',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    backgroundColor: '#f5f5f5',
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    {logSeleccionado.detalles}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </div>
      </DialogCustom>

      {/* Modal de limpieza */}
      <Modal
        title="Limpiar Logs Antiguos"
        open={cleanupModal}
        onOk={handleCleanup}
        onCancel={() => setCleanupModal(false)}
        okText="Eliminar"
        cancelText="Cancelar"
        okType="danger"
        confirmLoading={loading}
      >
        <p>¿Está seguro de que desea eliminar los logs de más de {cleanupDays} días?</p>
        <p>Esta acción no se puede deshacer.</p>
        <div style={{ marginTop: '16px' }}>
          <label htmlFor="cleanup-days-input">Días de antigüedad: </label>
          <Input
            id="cleanup-days-input"
            type="number"
            min="1"
            max="365"
            value={cleanupDays}
            onChange={(e) => setCleanupDays(parseInt(e.target.value) || 30)}
            style={{ width: '100px', marginLeft: '8px' }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Logs;
